import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { cloneDeep } from 'lodash-es';
import { DateTime } from 'luxon';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';

@Component({
  selector: 'app-modal-add-class-comment',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    EnfafModalComponent
  ],
  templateUrl: './modal-add-class-comment.component.html',
  styleUrl: './modal-add-class-comment.component.scss'
})
export class ModalAddClassCommentComponent implements OnInit, OnDestroy {
  commentForm: FormGroup = new FormGroup({});
  isSavingComment = false;
  showCloseAlert = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalAddClassCommentComponent>,
    private api: ApiService,
    private toast: ToastService,
    private alert: AlertService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer
  ) {
    this.iconRegistry.addSvgIcon('message-square-dark', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/message-square-dark.svg'));
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    this.commentForm = new FormGroup({
      // ENF-2314 — either courseClassId (normal class) or courseYearId (Clase 0) is set, never both;
      // saveComment() branches on which one is present to hit the right endpoint.
      courseClassId: new FormControl(receivedData?.courseClassId ?? null, receivedData?.courseClassId ? [Validators.required] : []),
      courseYearId: new FormControl(receivedData?.courseYearId ?? null, receivedData?.courseYearId ? [Validators.required] : []),
      message: new FormControl(null, [Validators.required]),
      parentCommentId: new FormControl(receivedData?.parentCommentId || null)
    });
    this.listenForChanges();
  }

  listenForChanges(): void {
    this.commentForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    });
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  async saveComment(): Promise<void> {
    try {
      this.isSavingComment = true;
      const { courseYearId, ...formValue } = this.commentForm.value;

      if (courseYearId) {
        // ENF-2314 — Clase 0 comment, separate endpoint/response shape from a normal class comment.
        const response = await firstValueFrom(
          this.api.createCourseYearClassZeroComment(courseYearId, {
            message: formValue.message,
            date: DateTime.now().toUTC().toISO(),
            parentCommentId: formValue.parentCommentId ?? undefined
          })
        );
        this.isSavingComment = false;
        this.closeModal({ commentUpdated: response.data });
        this.toast.presentSuccessToast('Comentario guardado');
        return;
      }

      const body = {
        ...formValue,
        date: DateTime.now().toUTC().toISO()
      };
      const response = await firstValueFrom(this.api.saveCourseClassComment(body));
      this.isSavingComment = false;
      this.closeModal({ commentUpdated: response.commentUpdated });
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.isSavingComment = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando tu comentario');
    }
  }
}
